import {
  type ChangeEvent,
  type CSSProperties,
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import styled from 'styled-components';
import type {
  DynamicTableColumnConfig,
  DynamicTableConfig,
  DynamicTableRowConfig,
  Question,
} from '../../config/types';
import type { DynamicTableExcelOptions } from '../../utils/dynamic-table-excel';
import {
  chainValidations,
  checkAllFieldsHaveValues,
  groupValidation,
  interpolateQuestionText,
  objectToString,
  stringToObject,
} from '../../utils/form-utils';
import Details from './Details';
import DynamicTableDurationInput from './DynamicTableDurationInput';
import DynamicTablesExcelToolbar from './DynamicTablesExcelToolbar';
import ErrorMessage from './ErrorMessage';
import {
  applyDynamicTableDurationValues,
  getDynamicTableCellFieldName,
  getDynamicTableColumnEditableFieldNames,
  getDynamicTableColumnStoredFieldNames,
  getDynamicTableRowCountAutoValueFieldName,
  markDynamicTableRowCountAsManual,
  prepopulateDynamicTableRowCounts,
} from './dynamic-table-derived-values';

type Props = {
  question: Question;
  disabled?: boolean;
  anchorId?: string;
  detailsHeading?: string;
  detailsContent?: string;
  interpolationValues?: Record<string, unknown>;
  onImport?: (value: string) => void | Promise<void>;
  showExcelToolbar?: boolean;
};

export const getDynamicTableFieldName = getDynamicTableCellFieldName;

const CONTINUOUS_ROW_PREFIX = 'continuousRow';
const CONTINUOUS_ROW_HEADER_SUFFIX = '__rowHeader';
const INITIAL_CONTINUOUS_EDITABLE_ROW_COUNT = 5;
const SELECT_ROW_HEADER_EXTRA_WIDTH = 56;
const SELECT_INPUT_EXTRA_WIDTH = 34;

export const getDynamicTableRowHeaderFieldName = (rowKey: string) =>
  `${rowKey}${CONTINUOUS_ROW_HEADER_SUFFIX}`;

const getContinuousRowIndex = (field: string) => {
  const match = field.match(new RegExp(`^${CONTINUOUS_ROW_PREFIX}(\\d+)`));
  return match?.[1] ? Number(match[1]) : undefined;
};

const getContinuousRowKey = (index: number) => `${CONTINUOUS_ROW_PREFIX}${index}`;

const isContinuousRowPopulated = (
  config: DynamicTableConfig,
  rowKey: string,
  values: Record<string, string>,
) =>
  hasValue(values[getDynamicTableRowHeaderFieldName(rowKey)]) ||
  config.columns.some((column) =>
    getDynamicTableColumnEditableFieldNames(rowKey, column).some((field) =>
      hasValue(values[field]),
    ),
  );

export const resolveContinuousDynamicTableConfig = (
  config: DynamicTableConfig,
  values: Record<string, string>,
  includeBlankRow = false,
  minimumContinuousRowCount = 0,
): DynamicTableConfig => {
  if (!config.continuous) return config;

  const populatedIndexes = Object.entries(values)
    .filter(([, value]) => hasValue(value))
    .map(([field]) => getContinuousRowIndex(field))
    .filter((index): index is number => index !== undefined);
  const populatedRowCount = populatedIndexes.length > 0 ? Math.max(...populatedIndexes) : 0;
  const rowCount = Math.max(
    populatedRowCount + (includeBlankRow ? 1 : 0),
    minimumContinuousRowCount,
  );
  const continuousRows: DynamicTableRowConfig[] = Array.from({ length: rowCount }, (_, index) => {
    const key = getContinuousRowKey(index + 1);
    return {
      key,
      label: config.rowHeaderInput
        ? (values[getDynamicTableRowHeaderFieldName(key)] ?? '')
        : String(index + 1),
    };
  });

  return {
    ...config,
    rows: [...continuousRows, ...config.rows.filter((row) => Boolean(row.calculation))],
  };
};

export const getDynamicTableEditableKeys = (config?: DynamicTableConfig) => {
  if (!config) return [];
  return config.rows
    .filter(isEditableRow)
    .flatMap((row) => [
      ...(config.continuous && config.rowHeaderInput
        ? [getDynamicTableRowHeaderFieldName(row.key)]
        : []),
      ...config.columns
        .filter(isEditableColumn)
        .flatMap((column) => getDynamicTableColumnEditableFieldNames(row.key, column)),
    ]);
};

const getDynamicTableRequiredKeys = (
  config: DynamicTableConfig,
  values: Record<string, string>,
) => {
  return config.rows
    .filter(
      (row) =>
        isEditableRow(row) &&
        (!config.continuous || isContinuousRowPopulated(config, row.key, values)),
    )
    .flatMap((row) => [
      ...(config.continuous && config.rowHeaderInput
        ? [getDynamicTableRowHeaderFieldName(row.key)]
        : []),
      ...config.columns
        .filter((column) => isEditableColumn(column) && column.required !== false)
        .flatMap((column) => getDynamicTableColumnEditableFieldNames(row.key, column)),
    ]);
};

export const getDynamicTableKeys = (config?: DynamicTableConfig) => {
  if (!config) return [];
  return config.rows.flatMap((row) => [
    ...(config.continuous && config.rowHeaderInput && isEditableRow(row)
      ? [getDynamicTableRowHeaderFieldName(row.key)]
      : []),
    ...config.columns.flatMap((column) => getDynamicTableColumnStoredFieldNames(row.key, column)),
    ...(config.rowCountsFrom
      ? [
          getDynamicTableRowCountAutoValueFieldName(
            row.key,
            config.rowCountsFrom.targetColumn,
          ),
        ]
      : []),
  ]);
};

const getDynamicTableRowSelections = (value: unknown): string[] => {
  if (Array.isArray(value)) return value.map(String);
  if (typeof value !== 'string' || !value.trim()) return [];

  try {
    return getDynamicTableRowSelections(JSON.parse(value));
  } catch (_) {
    return [value.trim()];
  }
};

export const filterDynamicTableRows = (
  config: DynamicTableConfig,
  answers: Record<string, unknown>,
): DynamicTableConfig => {
  if (!config.rowFilter) return config;

  const selectedValues = new Set(getDynamicTableRowSelections(answers[config.rowFilter.field]));
  const matchBy = config.rowFilter.match ?? 'label';

  return {
    ...config,
    rows: config.rows.filter((row) => {
      if (row.calculation) return true;
      const selectedValue = matchBy === 'key' ? row.key : row.label;
      if (!selectedValues.has(selectedValue)) return false;
      if (!config.rowFilter?.conditionalAnswers) return true;

      const { answerFieldsByOption, answerValue } = config.rowFilter.conditionalAnswers;
      const answerField = answerFieldsByOption[selectedValue];
      return Boolean(answerField) && String(answers[answerField] ?? '').trim() === answerValue;
    }),
  };
};

export const resolveDynamicTableRowHeaderOptions = (
  config: DynamicTableConfig,
  answers: Record<string, unknown>,
): DynamicTableConfig => {
  const optionsFrom = config.rowHeaderInput?.optionsFrom;
  if (!optionsFrom || !config.rowHeaderInput) return config;

  const selectedValues = getDynamicTableRowSelections(answers[optionsFrom.selectionField]);
  const options = selectedValues
    .filter((option) => {
      const answerField = optionsFrom.answerFieldsByOption[option];
      return (
        Boolean(answerField) &&
        String(answers[answerField] ?? '').trim() === optionsFrom.answerValue
      );
    })
    .sort((left, right) => left.localeCompare(right, 'en-GB', { sensitivity: 'base' }));

  return {
    ...config,
    rowHeaderInput: {
      ...config.rowHeaderInput,
      options,
    },
  };
};

export const resolveDynamicTableRowsFromAnswer = (
  config: DynamicTableConfig,
  answers: Record<string, unknown>,
): DynamicTableConfig => {
  if (!config.rowCountField) return config;

  const rowCountValue = Number(String(answers[config.rowCountField] ?? '').trim());
  const rowCount = Number.isSafeInteger(rowCountValue) && rowCountValue > 0 ? rowCountValue : 0;
  const rowKeyPrefix = config.rowKeyPrefix ?? 'row';
  const calculatedRows = config.rows.filter((row) => Boolean(row.calculation));

  return {
    ...config,
    rows: [
      ...Array.from({ length: rowCount }, (_, index) => ({
        key: `${rowKeyPrefix}${index + 1}`,
        label: String(index + 1),
      })),
      ...calculatedRows,
    ],
  };
};

const toNumber = (value: string | undefined) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

const isEditableColumn = (column: DynamicTableConfig['columns'][number]) =>
  column.enabled !== false && !column.calculation;

const isEditableRow = (row: DynamicTableConfig['rows'][number]) =>
  row.enabled !== false && !row.calculation;

const isNumericColumn = (column: DynamicTableConfig['columns'][number]) =>
  column.inputType === undefined || column.inputType === 'number';

const getEditableColumns = (config: DynamicTableConfig) => config.columns.filter(isEditableColumn);

const getEditableRows = (config: DynamicTableConfig) => config.rows.filter(isEditableRow);

const hasValue = (value: string | undefined) => value !== undefined && value !== '';

const removeBlankContinuousRows = (config: DynamicTableConfig, values: Record<string, string>) => {
  if (!config.continuous) return values;

  const blankRowKeys = new Set(
    getEditableRows(config)
      .filter((row) => !isContinuousRowPopulated(config, row.key, values))
      .flatMap((row) => [
        getDynamicTableRowHeaderFieldName(row.key),
        ...config.columns.flatMap((column) =>
          getDynamicTableColumnStoredFieldNames(row.key, column),
        ),
      ]),
  );

  return Object.fromEntries(Object.entries(values).filter(([key]) => !blankRowKeys.has(key)));
};

const isExplicitZero = (value: string | undefined) => hasValue(value) && toNumber(value) === 0;

const getRowTotal = (
  config: DynamicTableConfig,
  rowKey: string,
  values: Record<string, string>,
  sourceColumnKeys?: string[],
) =>
  getEditableColumns(config)
    .filter((column) => !sourceColumnKeys || sourceColumnKeys.includes(column.key))
    .reduce(
      (total, column) => total + toNumber(values[getDynamicTableFieldName(rowKey, column.key)]),
      0,
    );

const isRowFullyAnswered = (
  config: DynamicTableConfig,
  rowKey: string,
  values: Record<string, string>,
) =>
  getEditableColumns(config).every((column) =>
    hasValue(values[getDynamicTableFieldName(rowKey, column.key)]),
  );

const stripLeadingZeroes = (value: string, allowDecimal: boolean) => {
  const negative = value.startsWith('-');
  const unsignedValue = negative ? value.slice(1) : value;
  const [integerPart, decimalPart] = unsignedValue.split('.');
  const strippedInteger = integerPart.replace(/^0+(?=\d)/, '');
  const normalisedInteger =
    strippedInteger === '' && decimalPart !== undefined ? '0' : strippedInteger;
  const normalisedValue =
    allowDecimal && decimalPart !== undefined
      ? `${normalisedInteger}.${decimalPart}`
      : normalisedInteger;

  return `${negative ? '-' : ''}${normalisedValue}`;
};

export const normaliseDynamicTableInputValue = (
  value: string,
  row: Pick<DynamicTableRowConfig, 'allowDecimal' | 'allowNegativeNumbers'>,
  column?: Pick<DynamicTableColumnConfig, 'allowDecimal' | 'allowNegativeNumbers'>,
) => {
  const allowDecimal = row.allowDecimal === true || column?.allowDecimal === true;
  const allowNegativeNumbers =
    row.allowNegativeNumbers === true || column?.allowNegativeNumbers === true;
  let nextValue = '';
  let hasDecimal = false;

  for (const char of value) {
    if (char >= '0' && char <= '9') {
      nextValue += char;
      continue;
    }
    if (char === '.' && allowDecimal && !hasDecimal) {
      nextValue += char;
      hasDecimal = true;
      continue;
    }
    if (char === '-' && allowNegativeNumbers && nextValue === '') {
      nextValue += char;
    }
  }

  return stripLeadingZeroes(nextValue, allowDecimal);
};

export const applyDynamicTableCalculations = (
  config: DynamicTableConfig,
  values: Record<string, string>,
) => {
  const nextValues = { ...values };
  const editableColumns = getEditableColumns(config);

  config.rows.forEach((row) => {
    config.columns.forEach((column) => {
      if (column.calculation !== 'rowSum') return;

      const sourceColumns = editableColumns.filter(
        (sourceColumn) =>
          isNumericColumn(sourceColumn) &&
          (!column.sourceColumns || column.sourceColumns.includes(sourceColumn.key)),
      );
      const rowHasAnyValue = sourceColumns.some((sourceColumn) =>
        hasValue(nextValues[getDynamicTableFieldName(row.key, sourceColumn.key)]),
      );
      const total = getRowTotal(config, row.key, nextValues, column.sourceColumns);

      nextValues[getDynamicTableFieldName(row.key, column.key)] = rowHasAnyValue
        ? String(total)
        : '';
    });
  });

  const editableRows = getEditableRows(config);
  config.rows.forEach((row) => {
    if (row.calculation !== 'columnSum') return;

    config.columns.forEach((column) => {
      if (!isNumericColumn(column)) {
        nextValues[getDynamicTableFieldName(row.key, column.key)] = '';
        return;
      }
      const columnHasAnyValue = editableRows.some((sourceRow) =>
        hasValue(nextValues[getDynamicTableFieldName(sourceRow.key, column.key)]),
      );
      const total = editableRows.reduce(
        (sum, sourceRow) =>
          sum + toNumber(nextValues[getDynamicTableFieldName(sourceRow.key, column.key)]),
        0,
      );
      nextValues[getDynamicTableFieldName(row.key, column.key)] = columnHasAnyValue
        ? String(Number(total.toFixed(10)))
        : '';
    });
  });

  return nextValues;
};

export const applyDynamicTableConditions = (
  config: DynamicTableConfig,
  values: Record<string, string>,
) => {
  const nextValues = applyDynamicTableCalculations(config, values);

  config.rows.forEach((row) => {
    if (!row.lessThanOrEqualToRow) return;

    getEditableColumns(config).forEach((column) => {
      const sourceField = getDynamicTableFieldName(row.lessThanOrEqualToRow as string, column.key);
      const dependentField = getDynamicTableFieldName(row.key, column.key);

      if (isExplicitZero(nextValues[sourceField])) {
        nextValues[dependentField] = '0';
      }
    });
  });

  return applyDynamicTableDurationValues(config, applyDynamicTableCalculations(config, nextValues));
};

export const updateDynamicTableCellValues = (
  config: DynamicTableConfig,
  values: Record<string, string>,
  row: DynamicTableRowConfig,
  column: DynamicTableColumnConfig,
  field: string,
  inputValue: string,
) => {
  const updatedValues = {
    ...values,
    [field]:
      column.inputType === 'text' || column.inputType === 'select'
        ? inputValue
        : normaliseDynamicTableInputValue(inputValue, row, column),
  };

  return column.inputType === 'text'
    ? updatedValues
    : applyDynamicTableConditions(config, updatedValues);
};

export const validateDynamicTableConditions = (
  config: DynamicTableConfig,
  values: Record<string, string>,
): true | string => {
  const getRowErrorReference = (row: DynamicTableRowConfig) =>
    row.label.match(/^#\d+/)?.[0] ?? row.label;

  for (const row of config.rows) {
    if (!row.lessThanOrEqualToRow) continue;

    const sourceRow = config.rows.find((currentRow) => currentRow.key === row.lessThanOrEqualToRow);
    if (!sourceRow) continue;

    for (const column of getEditableColumns(config)) {
      const sourceField = getDynamicTableFieldName(sourceRow.key, column.key);
      const dependentField = getDynamicTableFieldName(row.key, column.key);
      if (!hasValue(values[sourceField]) || !hasValue(values[dependentField])) continue;

      if (toNumber(values[dependentField]) > toNumber(values[sourceField])) {
        return `The answer in row ${getRowErrorReference(
          row,
        )} must be less than or equal to the answer in row ${getRowErrorReference(sourceRow)}`;
      }
    }
  }

  return true;
};

const parseInitialValues = (config: DynamicTableConfig, defaultValue?: string) =>
  applyDynamicTableConditions(
    config,
    prepopulateContinuousRowHeaders(config, stringToObject(defaultValue)),
  );

const prepopulateContinuousRowHeaders = (
  config: DynamicTableConfig,
  values: Record<string, string>,
): Record<string, string> => {
  if (!config.continuous || !config.rowHeaderInput?.prepopulateOptions) return values;

  const options = config.rowHeaderInput.options ?? [];
  const existingHeaders = new Set(
    Object.entries(values)
      .filter(([field]) => field.endsWith(CONTINUOUS_ROW_HEADER_SUFFIX))
      .map(([, value]) => value)
      .filter(hasValue),
  );
  const missingOptions = options.filter((option) => !existingHeaders.has(option));
  if (!missingOptions.length) return values;

  const populatedIndexes = Object.keys(values)
    .map(getContinuousRowIndex)
    .filter((index): index is number => index !== undefined);
  let nextIndex = populatedIndexes.length > 0 ? Math.max(...populatedIndexes) + 1 : 1;
  const populatedValues = { ...values };
  missingOptions.forEach((option) => {
    populatedValues[getDynamicTableRowHeaderFieldName(getContinuousRowKey(nextIndex))] = option;
    nextIndex += 1;
  });
  return populatedValues;
};

export const normaliseDynamicTableSubmissionValues = (
  config: DynamicTableConfig,
  values: Record<string, string>,
  required = false,
) => {
  const resolvedConfig = resolveContinuousDynamicTableConfig(config, values);
  const validKeys = new Set(getDynamicTableKeys(resolvedConfig));
  const filteredValues = removeBlankContinuousRows(
    resolvedConfig,
    Object.fromEntries(Object.entries(values).filter(([key]) => validKeys.has(key))),
  );
  if (!required || resolvedConfig.strict === true) {
    return removeBlankContinuousRows(
      resolvedConfig,
      applyDynamicTableConditions(resolvedConfig, filteredValues),
    );
  }

  const valuesWithZeroDefaults = { ...filteredValues };
  getEditableRows(resolvedConfig).forEach((row) => {
    if (
      resolvedConfig.continuous &&
      !isContinuousRowPopulated(resolvedConfig, row.key, filteredValues)
    )
      return;
    getEditableColumns(resolvedConfig).forEach((column) => {
      if (!isNumericColumn(column)) return;
      const key = getDynamicTableFieldName(row.key, column.key);
      if (!hasValue(valuesWithZeroDefaults[key])) valuesWithZeroDefaults[key] = '0';
    });
  });
  return removeBlankContinuousRows(
    resolvedConfig,
    applyDynamicTableConditions(resolvedConfig, valuesWithZeroDefaults),
  );
};

export const sortContinuousDynamicTableSubmissionValue = (
  config: DynamicTableConfig,
  value: unknown,
): unknown => {
  if (!config.continuous || config.sortOnSaveBy !== 'rowHeader' || typeof value !== 'string')
    return value;

  const values = stringToObject(value);
  const rows = new Map<number, Array<[string, string]>>();
  const otherValues: Record<string, string> = {};

  Object.entries(values).forEach(([field, fieldValue]) => {
    const match = field.match(/^continuousRow(\d+)(.*)$/);
    if (!match?.[1]) {
      otherValues[field] = fieldValue;
      return;
    }
    const rowIndex = Number(match[1]);
    const rowFields = rows.get(rowIndex) ?? [];
    rowFields.push([match[2], fieldValue]);
    rows.set(rowIndex, rowFields);
  });

  const sortedRows = [...rows.entries()].sort(
    ([leftIndex, leftFields], [rightIndex, rightFields]) => {
      const leftHeader =
        leftFields.find(([suffix]) => suffix === CONTINUOUS_ROW_HEADER_SUFFIX)?.[1] ?? '';
      const rightHeader =
        rightFields.find(([suffix]) => suffix === CONTINUOUS_ROW_HEADER_SUFFIX)?.[1] ?? '';
      const labelComparison = leftHeader.localeCompare(rightHeader, 'en-GB', {
        sensitivity: 'base',
      });
      return labelComparison || leftIndex - rightIndex;
    },
  );

  const sortedValues = { ...otherValues };
  sortedRows.forEach(([, rowFields], index) => {
    rowFields.forEach(([suffix, fieldValue]) => {
      sortedValues[`${getContinuousRowKey(index + 1)}${suffix}`] = fieldValue;
    });
  });
  return objectToString(sortedValues);
};

const hasAnyEditableValue = (config: DynamicTableConfig, values: Record<string, string>) =>
  getDynamicTableEditableKeys(config).some(
    (key) => values[key] !== undefined && values[key] !== '',
  );

const containsTargetValue = (value: unknown, targetValue: string) =>
  String(value ?? '').includes(targetValue);

type HeaderLineLimit = 1 | 2 | 3 | 4;

export type DynamicTableSizing = {
  lineLimit: HeaderLineLimit;
  columnWidths: Record<string, number>;
  rowHeaderWidth: number;
  tableWidth: number;
  requiresScroll: boolean;
};

type DynamicTableSizingOptions = {
  columns: DynamicTableColumnConfig[];
  rowLabels: string[];
  rowHeader: string;
  rowHeaderInputType?: NonNullable<DynamicTableConfig['rowHeaderInput']>['inputType'];
  rowHeaderOptions?: string[];
  availableWidth: number;
  measureText: (text: string) => number;
  cellChromeWidth?: number;
  extraWidthByColumn?: Record<string, number>;
  headerLineLimit?: HeaderLineLimit;
  showRowHeader?: boolean;
};

export const GROUP_SEPARATOR_WIDTH = 4;

const getWrappedLineCount = (
  text: string,
  contentWidth: number,
  measureText: (text: string) => number,
) => {
  const words = text.trim().split(/\s+/).filter(Boolean);
  if (words.length === 0) return 1;

  let lineCount = 1;
  let line = words[0];

  words.slice(1).forEach((word) => {
    const candidate = `${line} ${word}`;
    if (measureText(candidate) <= contentWidth) {
      line = candidate;
      return;
    }
    lineCount += 1;
    line = word;
  });

  return lineCount;
};

const getTextWidthForLineLimit = (
  text: string,
  lineLimit: HeaderLineLimit,
  measureText: (text: string) => number,
) => {
  const words = text.trim().split(/\s+/).filter(Boolean);
  if (words.length === 0) return 0;

  const longestWordWidth = Math.max(...words.map(measureText));
  const singleLineWidth = measureText(words.join(' '));
  if (lineLimit === 1 || longestWordWidth === singleLineWidth) return singleLineWidth;

  let minimumWidth = Math.ceil(longestWordWidth);
  let maximumWidth = Math.ceil(singleLineWidth);

  while (minimumWidth < maximumWidth) {
    const candidateWidth = Math.floor((minimumWidth + maximumWidth) / 2);
    if (getWrappedLineCount(text, candidateWidth, measureText) <= lineLimit) {
      maximumWidth = candidateWidth;
    } else {
      minimumWidth = candidateWidth + 1;
    }
  }

  return minimumWidth;
};

export const calculateDynamicTableSizing = ({
  columns,
  rowLabels,
  rowHeader,
  rowHeaderInputType,
  rowHeaderOptions = [],
  availableWidth,
  measureText,
  cellChromeWidth = 17,
  extraWidthByColumn = {},
  headerLineLimit,
  showRowHeader = true,
}: DynamicTableSizingOptions): DynamicTableSizing => {
  const standardRowHeaderWidth =
    Math.max(
      ...[rowHeader, ...rowLabels].map((label) =>
        getTextWidthForLineLimit(label, headerLineLimit ?? 4, measureText),
      ),
      0,
    ) + cellChromeWidth;
  const selectRowHeaderWidth =
    rowHeaderInputType === 'select'
      ? Math.max(...['Please select', ...rowHeaderOptions].map(measureText), 0) +
        cellChromeWidth +
        SELECT_ROW_HEADER_EXTRA_WIDTH
      : 0;
  const rowHeaderWidth = showRowHeader ? Math.max(standardRowHeaderWidth, selectRowHeaderWidth) : 0;

  const getSizing = (lineLimit: HeaderLineLimit) => {
    const columnWidths = Object.fromEntries(
      columns.map((column) => {
        const selectOptionWidth =
          column.inputType === 'select'
            ? Math.max(
                ...[column.emptyOptionLabel ?? 'Please select', ...(column.options ?? [])].map(
                  (option) => measureText(`${option}\u00a0`),
                ),
                0,
              ) + SELECT_INPUT_EXTRA_WIDTH
            : 0;
        return [
          column.key,
          Math.max(
            getTextWidthForLineLimit(
              column.label,
              column.headerLineLimit ?? lineLimit,
              measureText,
            ) +
              cellChromeWidth +
              (extraWidthByColumn[column.key] ?? 0),
            selectOptionWidth,
            column.minWidth ?? 0,
          ),
        ];
      }),
    );
    let tableWidth =
      rowHeaderWidth + columns.reduce((total, column) => total + columnWidths[column.key], 0);
    const fillColumns = columns.filter((column) => column.fillRemainingWidth);
    const remainingWidth = Math.max(availableWidth - tableWidth, 0);
    if (remainingWidth > 0 && fillColumns.length > 0) {
      const extraWidth = remainingWidth / fillColumns.length;
      fillColumns.forEach((column) => {
        columnWidths[column.key] += extraWidth;
      });
      tableWidth += remainingWidth;
    }

    return { lineLimit, columnWidths, rowHeaderWidth, tableWidth };
  };

  if (headerLineLimit) {
    const sizing = getSizing(headerLineLimit);
    return { ...sizing, requiresScroll: sizing.tableWidth > availableWidth };
  }

  for (const lineLimit of [1, 2, 3, 4] as HeaderLineLimit[]) {
    const sizing = getSizing(lineLimit);
    if (sizing.tableWidth <= availableWidth) {
      return { ...sizing, requiresScroll: false };
    }
  }

  return { ...getSizing(4), requiresScroll: true };
};

export const getSizingKey = (columns: DynamicTableColumnConfig[]) =>
  columns.map((column) => column.key).join('|');

export const getDynamicTableAvailableWidth = (
  containingWidth: number,
  pageWidth: number,
  scrollbarWidth: number,
) => Math.max((containingWidth > 0 ? containingWidth : pageWidth) - scrollbarWidth, 0);

export const positionBoundedGroupLabels = (viewport: HTMLElement) => {
  const viewportBounds = viewport.getBoundingClientRect();
  const table = viewport.querySelector<HTMLElement>('.dynamic-table--sticky-row-header');
  const rowHeader = table?.querySelector<HTMLElement>('thead .dynamic-table__row-header');
  const frozenBoundary = Math.max(
    viewportBounds.left,
    rowHeader?.getBoundingClientRect().right ?? viewportBounds.left,
  );

  viewport.querySelectorAll<HTMLElement>('.dynamic-table__group-header').forEach((header) => {
    const headerBounds = header.getBoundingClientRect();
    const label = header.querySelector<HTMLElement>('.dynamic-table__group-label');
    if (!label) return;

    const headerStyle = window.getComputedStyle(header);
    const borderLeft = Number.parseFloat(headerStyle.borderLeftWidth) || 0;
    const borderRight = Number.parseFloat(headerStyle.borderRightWidth) || 0;
    const paddingLeft = Number.parseFloat(headerStyle.paddingLeft) || 0;
    const paddingRight = Number.parseFloat(headerStyle.paddingRight) || 0;
    const groupLeft = headerBounds.left + borderLeft + paddingLeft;
    const groupRight = headerBounds.right - borderRight - paddingRight;
    const labelWidth = label.getBoundingClientRect().width;
    const maximumLeft = Math.max(groupLeft, groupRight - labelWidth);
    const targetLeft = Math.min(Math.max(frozenBoundary + paddingLeft, groupLeft), maximumLeft);

    header.style.setProperty('--dynamic-table-group-label-offset', `${targetLeft - groupLeft}px`);
  });
};

export const TableViewport = styled.div`
    position: relative;
    max-width: 100%;
    overflow: auto;
    scroll-behavior: smooth;
    overscroll-behavior-inline: contain;
    box-shadow:
        inset 12px 0 10px -14px #0b0c0c,
        inset -12px 0 10px -14px #0b0c0c;
`;

export const TableRegion = styled.div`
    width: min(var(--dynamic-table-width, 100%), calc(100vw - 2cm));
    min-width: 100%;
    max-width: calc(100vw - 2cm);

    .dynamic-table__caption {
        margin: 0;
        text-align: center;
    }
`;

export const TableCaption = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    margin-bottom: 10px;
`;

export const Table = styled.table`
    &.dynamic-table--compact {
        table-layout: fixed;
        min-width: 100%;
        border-collapse: separate;
        border-spacing: 0;
        margin-bottom: 8px;
    }

    &.dynamic-table--compact .govuk-table__header,
    &.dynamic-table--compact .govuk-table__cell {
        box-sizing: border-box;
        padding: 0;
        border: 0;
        border-right: 1px solid #b1b4b6;
        border-bottom: 1px solid #b1b4b6;
        background: #ffffff;
        vertical-align: middle;
    }

    &.dynamic-table--compact tr > :first-child {
        border-left: 1px solid #b1b4b6;
    }

    &.dynamic-table--compact thead tr:first-child > * {
        border-top: 1px solid #b1b4b6;
    }

    &.dynamic-table--sticky-headers thead {
        position: sticky;
        top: 0;
        z-index: 7;
    }

    &.dynamic-table--compact .dynamic-table__group-header,
    &.dynamic-table--compact .dynamic-table__column-header,
    &.dynamic-table--compact .dynamic-table__row-header,
    &.dynamic-table--compact .dynamic-table__hint-label,
    &.dynamic-table--compact .dynamic-table__total-label {
        padding: 6px 8px;
    }

    &.dynamic-table--compact .dynamic-table__group-header {
        position: relative;
        background: #e5e5e5;
        font-size: 16px;
        line-height: 1.25;
        overflow: hidden;
        text-align: left;
    }

    &.dynamic-table--compact .dynamic-table__group-label {
        position: relative;
        left: var(--dynamic-table-group-label-offset, 0px);
        display: inline-flex;
        align-items: center;
        gap: 8px;
        width: max-content;
        max-width: 100%;
        white-space: nowrap;
    }

    &.dynamic-table--compact .dynamic-table__column-header {
        background: #f3f2f1;
        font-size: 16px;
        line-height: 1.25;
        overflow-wrap: normal;
        word-break: normal;
        hyphens: none;
        text-align: left;
    }

    &.dynamic-table--compact .dynamic-table__column-header--nowrap {
        white-space: nowrap;
    }

    &.dynamic-table--compact .dynamic-table__hint-label,
    &.dynamic-table--compact .dynamic-table__hint-cell {
        background: #f3f2f1;
        font-size: 16px;
        line-height: 1.25;
    }

    &.dynamic-table--compact .dynamic-table__hint-cell {
        min-height: 44px;
        padding: 8px 10px;
    }

    &.dynamic-table--compact .dynamic-table__row-header {
        background: #ffffff;
        font-size: 16px;
        line-height: 1.25;
        overflow-wrap: normal;
        word-break: normal;
        hyphens: none;
    }

    &.dynamic-table--compact .dynamic-table__row-header--input {
        padding: 0;
    }

    &.dynamic-table--compact .dynamic-table__continuous-placeholder > * {
        background: #fffbe6;
    }

    &.dynamic-table--compact .dynamic-table__add-row-cell {
        padding: 0;
        background: #fffbe6;
    }

    &.dynamic-table--compact .dynamic-table__add-row-button {
        display: block;
        width: 100%;
        min-height: 44px;
        padding: 8px;
        border: 0;
        background: #fffbe6;
        color: #0b0c0c;
        font: inherit;
        font-weight: 700;
        text-align: left;
        cursor: pointer;
    }

    &.dynamic-table--compact .dynamic-table__add-row-button:hover {
        background: #fff7bf;
    }

    &.dynamic-table--add-row-light-blue .dynamic-table__continuous-placeholder > *,
    &.dynamic-table--add-row-light-blue .dynamic-table__add-row-cell,
    &.dynamic-table--add-row-light-blue .dynamic-table__add-row-button {
        background: #d2e2f1;
    }

    &.dynamic-table--add-row-light-blue .dynamic-table__add-row-button:hover {
        background: #b1c9dd;
    }

    &.dynamic-table--compact .dynamic-table__add-row-button:focus {
        position: relative;
        z-index: 6;
        outline: 3px solid #0b0c0c;
        outline-offset: -3px;
        box-shadow: inset 0 0 0 5px #ffdd00;
    }

    &.dynamic-table--compact .dynamic-table__add-row-button:disabled {
        cursor: default;
    }

    &.dynamic-table--compact .dynamic-table__row-header-input {
        display: block;
        width: 100%;
        min-width: 0;
        max-width: none;
        min-height: 44px;
        margin: 0;
        padding: 4px 8px;
        border: 0;
        border-radius: 0;
        background: transparent;
        line-height: 1.25;
        resize: none;
    }

    &.dynamic-table--compact .dynamic-table__row-header-input:focus {
        position: relative;
        z-index: 6;
        outline: 3px solid #0b0c0c;
        outline-offset: -3px;
        box-shadow: inset 0 0 0 5px #ffdd00;
        background: #ffffff;
    }

    &.dynamic-table--sticky-row-header .dynamic-table__row-header,
    &.dynamic-table--sticky-row-header .dynamic-table__hint-label,
    &.dynamic-table--sticky-row-header .dynamic-table__total-label {
        position: sticky;
        left: 0;
        z-index: 5;
    }

    &.dynamic-table--sticky-row-header thead .dynamic-table__row-header,
    &.dynamic-table--sticky-row-header thead .dynamic-table__hint-label {
        z-index: 9;
    }

    &.dynamic-table--compact .dynamic-table__group-start {
        border-left: ${GROUP_SEPARATOR_WIDTH}px solid #505a5f;
    }

    &.dynamic-table--compact .dynamic-table__active-row {
        background: #fffbe6;
    }

    &.dynamic-table--compact .dynamic-table__calculated-cell {
        background: #f3f2f1;
    }

    &.dynamic-table--compact .govuk-form-group {
        margin: 0;
    }

    &.dynamic-table--compact .dynamic-table__input {
        display: block;
        width: 100%;
        min-width: 0;
        max-width: none;
        min-height: 44px;
        margin: 0;
        padding: 4px 8px;
        border: 0;
        border-radius: 0;
        background: transparent;
        text-align: right;
        font-variant-numeric: tabular-nums;
    }

    &.dynamic-table--compact .dynamic-table__input--text {
        text-align: left;
    }

    &.dynamic-table--compact .dynamic-table__input--select {
        display: block;
        box-sizing: border-box;
        width: 100%;
        min-width: 0;
        max-width: none;
        margin: 2px 0;
    }

    &.dynamic-table--compact .dynamic-table__read-only-value {
        display: flex;
        box-sizing: border-box;
        align-items: center;
        justify-content: flex-end;
    }

    &.dynamic-table--compact .dynamic-table__read-only-value.dynamic-table__input--text {
        justify-content: flex-start;
    }

    &.dynamic-table--compact .dynamic-table__input:focus {
        position: relative;
        z-index: 6;
        outline: 3px solid #0b0c0c;
        outline-offset: -3px;
        box-shadow: inset 0 0 0 5px #ffdd00;
        background: #ffffff;
    }

    &.dynamic-table--compact .dynamic-table__input:disabled {
        background: #f3f2f1;
        color: #0b0c0c;
        opacity: 1;
    }

    &.dynamic-table--compact tfoot .govuk-table__header,
    &.dynamic-table--compact tfoot .govuk-table__cell {
        background: #f3f2f1;
        font-weight: 700;
    }
`;

type ActiveCell = {
  rowKey: string;
  columnKey: string;
};

export function DynamicTable({
  question,
  disabled = false,
  anchorId = useId(),
  detailsHeading,
  detailsContent,
  interpolationValues: externalInterpolationValues,
  onImport,
  showExcelToolbar = true,
}: Props) {
  const { register, getFieldState, getValues, setValue, control } = useFormContext();
  const { error } = getFieldState(question.name);
  const watchedQuestionValue = useWatch({ control, name: question.name }) as string | undefined;
  const watchedRowCount = useWatch({
    control,
    name: question.dynamicTable?.rowCountField ?? '__dynamicTableUnusedRowCount',
  });
  const watchedRowCountsSource = useWatch({
    control,
    name: question.dynamicTable?.rowCountsFrom?.question ?? '__dynamicTableUnusedCountSource',
  });
  const rowHeaderOptionsFrom = question.dynamicTable?.rowHeaderInput?.optionsFrom;
  const rowHeaderOptionFieldNames = useMemo(
    () =>
      rowHeaderOptionsFrom
        ? [
            ...new Set([
              rowHeaderOptionsFrom.selectionField,
              ...Object.values(rowHeaderOptionsFrom.answerFieldsByOption),
            ]),
          ]
        : [],
    [rowHeaderOptionsFrom],
  );
  // Only controller answers can change row options. Watching every form value rebuilds the table per keystroke.
  const watchedRowHeaderOptionValues = useWatch({
    control,
    name: rowHeaderOptionFieldNames,
  }) as unknown[];
  const watchedRowHeaderOptionValuesKey = JSON.stringify(watchedRowHeaderOptionValues ?? []);
  const rowHeaderOptionAnswers = useMemo(() => {
    const optionValues = JSON.parse(watchedRowHeaderOptionValuesKey) as unknown[];
    return Object.fromEntries(
      rowHeaderOptionFieldNames.map((field, index) => [field, optionValues[index]]),
    );
  }, [rowHeaderOptionFieldNames, watchedRowHeaderOptionValuesKey]);
  const baseConfig = useMemo(
    () =>
      question.dynamicTable
        ? resolveDynamicTableRowsFromAnswer(
            resolveDynamicTableRowHeaderOptions(question.dynamicTable, rowHeaderOptionAnswers),
            {
              [question.dynamicTable.rowCountField ?? '']: watchedRowCount,
            },
          )
        : undefined,
    [question.dynamicTable, rowHeaderOptionAnswers, watchedRowCount],
  );
  const interpolationValues = {
    ...(externalInterpolationValues || {}),
    ...(getValues() as Record<string, unknown>),
  };

  const initialValues = useMemo(() => {
    if (!baseConfig) return {};
    const currentValue = getValues(question.name);
    const populatedValues = prepopulateDynamicTableRowCounts(
      baseConfig,
      watchedRowCountsSource,
      stringToObject(currentValue),
    );
    return parseInitialValues(
      resolveContinuousDynamicTableConfig(baseConfig, populatedValues, true),
      objectToString(populatedValues),
    );
  }, [baseConfig, getValues, question.name, watchedRowCountsSource]);
  const [values, setValues] = useState(initialValues);
  const [minimumContinuousRowCount, setMinimumContinuousRowCount] = useState(
    (question.dynamicTable?.initialContinuousEditableRowCount ??
      INITIAL_CONTINUOUS_EDITABLE_ROW_COUNT) + 1,
  );
  const config = useMemo(
    () =>
      baseConfig
        ? resolveContinuousDynamicTableConfig(
            baseConfig,
            values,
            !disabled,
            disabled ? 0 : minimumContinuousRowCount,
          )
        : undefined,
    [baseConfig, disabled, minimumContinuousRowCount, values],
  );
  const [activeCell, setActiveCell] = useState<ActiveCell>();
  const [tableSizings, setTableSizings] = useState<Record<string, DynamicTableSizing>>({});
  const [tableScrollbarWidth, setTableScrollbarWidth] = useState(0);
  const [tableOverflowByKey, setTableOverflowByKey] = useState<Record<string, boolean>>({});
  const tableRootRef = useRef<HTMLDivElement>(null);
  const lastLocalValueRef = useRef<string>();
  const sizingColumns = config?.columns;
  const sizingGroups = config?.groups;
  const sizingRowHeader = config?.rowHeader ?? 'Question';
  const sizingHeaderLineLimit = config?.headerLineLimit;
  const sizingRowHeaderInputType = config?.rowHeaderInput?.inputType;
  const sizingRowLabelsKey = JSON.stringify(config?.rows.map((row) => row.label) ?? []);
  const sizingRowHeaderOptionsKey = JSON.stringify(config?.rowHeaderInput?.options ?? []);
  const sizingRowLabels = useMemo(
    () => JSON.parse(sizingRowLabelsKey) as string[],
    [sizingRowLabelsKey],
  );
  const sizingRowHeaderOptions = useMemo(
    () => JSON.parse(sizingRowHeaderOptionsKey) as string[],
    [sizingRowHeaderOptionsKey],
  );
  const showRowHeader = config?.showRowHeader !== false;

  const updateTableViewports = useCallback(() => {
    const nextOverflowByKey: Record<string, boolean> = {};
    tableRootRef.current
      ?.querySelectorAll<HTMLElement>('[data-testid="dynamic-table-viewport"]')
      .forEach((viewport) => {
        const tableKey = viewport.dataset.tableKey;
        if (tableKey) nextOverflowByKey[tableKey] = viewport.scrollWidth > viewport.clientWidth;
        positionBoundedGroupLabels(viewport);
      });
    setTableOverflowByKey((current) => {
      const keys = Object.keys(nextOverflowByKey);
      return keys.length === Object.keys(current).length &&
        keys.every((key) => current[key] === nextOverflowByKey[key])
        ? current
        : nextOverflowByKey;
    });
  }, []);

  useEffect(() => {
    if (!baseConfig || watchedQuestionValue === undefined) return;
    if (watchedQuestionValue === lastLocalValueRef.current) return;
    const watchedValues = stringToObject(watchedQuestionValue);
    setValues(
      parseInitialValues(
        resolveContinuousDynamicTableConfig(baseConfig, watchedValues, true),
        watchedQuestionValue,
      ),
    );
  }, [baseConfig, watchedQuestionValue]);

  useEffect(() => {
    if (!baseConfig?.rowCountsFrom) return;

    const currentValue = getValues(question.name);
    const populatedValues = prepopulateDynamicTableRowCounts(
      baseConfig,
      watchedRowCountsSource,
      stringToObject(currentValue),
    );
    const populatedValue = objectToString(populatedValues);
    if (currentValue === populatedValue) return;

    lastLocalValueRef.current = populatedValue;
    setValues(parseInitialValues(baseConfig, populatedValue));
    setValue(question.name, populatedValue, { shouldDirty: false });
  }, [
    baseConfig,
    getValues,
    question.name,
    setValue,
    watchedQuestionValue,
    watchedRowCountsSource,
  ]);

  useEffect(() => {
    if (!baseConfig || !question.dynamicTable?.rowCountField) return;

    const currentValue = getValues(question.name);
    const currentValues = stringToObject(currentValue);
    const validKeys = new Set(getDynamicTableKeys(baseConfig));
    if (!Object.keys(currentValues).some((key) => !validKeys.has(key))) return;

    const cleanedValues = normaliseDynamicTableSubmissionValues(
      baseConfig,
      currentValues,
      question.rules?.required,
    );
    const cleanedValue = hasAnyEditableValue(baseConfig, cleanedValues)
      ? objectToString(cleanedValues)
      : '';
    lastLocalValueRef.current = cleanedValue;
    setValues(parseInitialValues(baseConfig, cleanedValue));
    setValue(question.name, cleanedValue, { shouldDirty: true });
  }, [
    baseConfig,
    getValues,
    question.dynamicTable?.rowCountField,
    question.name,
    question.rules?.required,
    setValue,
  ]);

  useEffect(() => {
    if (!baseConfig) return;
    const currentValue = getValues(question.name);
    if (question.rules?.required && baseConfig.strict !== true) {
      const submissionValue = objectToString(
        normaliseDynamicTableSubmissionValues(
          baseConfig,
          prepopulateDynamicTableRowCounts(
            baseConfig,
            watchedRowCountsSource,
            prepopulateContinuousRowHeaders(baseConfig, stringToObject(currentValue)),
          ),
          question.rules.required,
        ),
      );
      if (currentValue !== submissionValue) {
        lastLocalValueRef.current = submissionValue;
        setValue(question.name, submissionValue, { shouldDirty: false });
      }
      return;
    }
    if (currentValue === undefined) setValue(question.name, '');
  }, [
    baseConfig,
    getValues,
    question.name,
    question.rules?.required,
    setValue,
    watchedRowCountsSource,
  ]);

  useEffect(() => {
    if (!sizingColumns || typeof document === 'undefined') return undefined;

    let cancelled = false;
    let frameId: number | undefined;
    const updateTableSizing = () => {
      if (cancelled) return;

      const measurement = document.createElement('span');
      measurement.setAttribute('aria-hidden', 'true');
      Object.assign(measurement.style, {
        position: 'absolute',
        visibility: 'hidden',
        whiteSpace: 'nowrap',
        fontFamily: 'GDS Transport, arial, sans-serif',
        fontSize: '16px',
        fontWeight: '700',
        lineHeight: '20px',
      });
      document.body.appendChild(measurement);
      const measureText = (text: string) => {
        measurement.textContent = text;
        return measurement.getBoundingClientRect().width;
      };

      const gutter = document.createElement('span');
      Object.assign(gutter.style, {
        position: 'absolute',
        visibility: 'hidden',
        width: '1cm',
      });
      document.body.appendChild(gutter);

      const scrollbar = document.createElement('span');
      Object.assign(scrollbar.style, {
        position: 'absolute',
        visibility: 'hidden',
        width: '100px',
        height: '100px',
        overflow: 'scroll',
        scrollbarGutter: 'stable',
      });
      document.body.appendChild(scrollbar);
      const scrollbarWidth = scrollbar.offsetWidth - scrollbar.clientWidth;

      const containingWidth = tableRootRef.current?.clientWidth ?? 0;
      const pageWidth = Math.max(
        document.documentElement.clientWidth - gutter.getBoundingClientRect().width * 2,
        0,
      );
      const availableWidth = getDynamicTableAvailableWidth(
        containingWidth,
        pageWidth,
        scrollbarWidth,
      );
      const columnSets = [
        sizingColumns,
        ...(sizingGroups ?? []).map((group) =>
          group.columnKeys
            .map((key) => sizingColumns.find((column) => column.key === key))
            .filter((column): column is DynamicTableColumnConfig => Boolean(column)),
        ),
      ];
      const groupedSeparatorWidths = Object.fromEntries(
        (sizingGroups ?? []).slice(1).map((group) => [group.columnKeys[0], GROUP_SEPARATOR_WIDTH]),
      );
      const nextSizings = Object.fromEntries(
        columnSets.map((columns, index) => [
          getSizingKey(columns),
          calculateDynamicTableSizing({
            columns,
            rowLabels: sizingRowLabels,
            rowHeader: sizingRowHeader,
            rowHeaderInputType: sizingRowHeaderInputType,
            rowHeaderOptions: sizingRowHeaderOptions,
            availableWidth,
            measureText,
            extraWidthByColumn: index === 0 ? groupedSeparatorWidths : undefined,
            headerLineLimit: sizingHeaderLineLimit,
            showRowHeader,
          }),
        ]),
      );

      measurement.remove();
      gutter.remove();
      scrollbar.remove();
      setTableScrollbarWidth(scrollbarWidth);
      setTableSizings(nextSizings);
      frameId = window.requestAnimationFrame(() => {
        updateTableViewports();
      });
    };

    updateTableSizing();
    window.addEventListener('resize', updateTableSizing);
    document.fonts?.ready.then(updateTableSizing);

    return () => {
      cancelled = true;
      window.removeEventListener('resize', updateTableSizing);
      if (frameId !== undefined) window.cancelAnimationFrame(frameId);
    };
  }, [
    sizingColumns,
    sizingGroups,
    sizingHeaderLineLimit,
    sizingRowHeader,
    sizingRowHeaderInputType,
    sizingRowHeaderOptions,
    sizingRowLabels,
    showRowHeader,
    updateTableViewports,
  ]);

  useEffect(() => {
    const frameId = window.requestAnimationFrame(() => {
      updateTableViewports();
    });

    return () => window.cancelAnimationFrame(frameId);
  }, [tableSizings, updateTableViewports]);

  const { onChange, name } = register(question.name, {
    validate: (value: string) => {
      if (!baseConfig) return true;
      const rawValues = stringToObject(value);
      const validationConfig = resolveContinuousDynamicTableConfig(baseConfig, rawValues);
      const parsedValue = normaliseDynamicTableSubmissionValues(
        baseConfig,
        rawValues,
        question.rules?.required,
      );
      return chainValidations([
        question.rules?.required &&
        validationConfig.continuous &&
        getEditableRows(validationConfig).length === 0
          ? 'Please add at least one row.'
          : true,
        question.rules?.required && getEditableRows(validationConfig).length > 0
          ? checkAllFieldsHaveValues({
              value: parsedValue,
              keys: getDynamicTableRequiredKeys(validationConfig, parsedValue),
            })
          : true,
        validateDynamicTableConditions(validationConfig, parsedValue),
        groupValidation({
          value: parsedValue,
          question,
          sumColumns: getDynamicTableKeys(validationConfig),
        }),
      ]);
    },
  });

  const triggerChange = (updatedValues: Record<string, string>, changedTextField?: string) => {
    if (!baseConfig) return '';
    const populatedConfig = resolveContinuousDynamicTableConfig(baseConfig, updatedValues);
    const submissionValues = changedTextField
      ? {
          ...stringToObject(getValues(question.name)),
          [changedTextField]: updatedValues[changedTextField] ?? '',
        }
      : normaliseDynamicTableSubmissionValues(baseConfig, updatedValues, question.rules?.required);
    const value =
      question.rules?.required && baseConfig.strict !== true
        ? objectToString(submissionValues)
        : hasAnyEditableValue(populatedConfig, updatedValues)
          ? objectToString(submissionValues)
          : '';
    lastLocalValueRef.current = value;
    onChange({ target: { name, value } });
    if (!changedTextField) applyDynamicTableTargetEffects(submissionValues);
    return value;
  };

  const applyDynamicTableTargetEffects = (updatedValues: Record<string, string>) => {
    if (!config) return;

    config.rows.forEach((row) => {
      const targetConfig = row.setQuestionWhenRowTotalEquals;
      if (!targetConfig || !isRowFullyAnswered(config, row.key, updatedValues)) return;

      const currentTargetValue = getValues(targetConfig.targetQuestion);
      const matchesTargetValue = getRowTotal(config, row.key, updatedValues) === targetConfig.value;
      if (matchesTargetValue && String(currentTargetValue ?? '') !== targetConfig.targetValue) {
        setValue(targetConfig.targetQuestion, targetConfig.targetValue, {
          shouldDirty: false,
          shouldValidate: true,
        });
        return;
      }
      if (
        !matchesTargetValue &&
        containsTargetValue(currentTargetValue, targetConfig.targetValue)
      ) {
        setValue(targetConfig.targetQuestion, '', {
          shouldDirty: false,
          shouldValidate: true,
        });
      }
    });
  };

  const updateCellValue = (
    row: DynamicTableRowConfig,
    column: DynamicTableColumnConfig,
    field: string,
    value: string,
  ) => {
    if (!config) return;
    const updatedValues = markDynamicTableRowCountAsManual(
      config,
      row.key,
      column.key,
      updateDynamicTableCellValues(config, values, row, column, field, value),
    );
    setValues(updatedValues);
    triggerChange(updatedValues, column.inputType === 'text' ? field : undefined);
  };

  const updateRawCellValue = (field: string, value: string) => {
    if (!config) return;
    const updatedValues = applyDynamicTableConditions(config, { ...values, [field]: value });
    setValues(updatedValues);
    triggerChange(updatedValues);
  };

  const handleValueChange =
    (row: DynamicTableRowConfig, column: DynamicTableColumnConfig, field: string) =>
    (event: ChangeEvent<HTMLInputElement>) =>
      updateCellValue(row, column, field, event.target.value);

  const handleRowHeaderChange =
    (row: DynamicTableRowConfig) =>
    (event: ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
      if (!config) return;
      const updatedValues = applyDynamicTableConditions(config, {
        ...values,
        [getDynamicTableRowHeaderFieldName(row.key)]: event.target.value,
      });
      setValues(updatedValues);
      triggerChange(updatedValues);
    };

  const generateInputId = (field: string) => `${anchorId}-${field}`;

  const activateContinuousRow = (row: DynamicTableRowConfig) => {
    const rowIndex = getContinuousRowIndex(row.key);
    if (!rowIndex) return;
    setMinimumContinuousRowCount((current) => Math.max(current, rowIndex + 1));
    window.requestAnimationFrame(() => {
      const firstEditableColumn = config?.columns.find(isEditableColumn);
      const firstField =
        showRowHeader && config?.rowHeaderInput
          ? getDynamicTableRowHeaderFieldName(row.key)
          : firstEditableColumn
            ? getDynamicTableFieldName(row.key, firstEditableColumn.key)
            : undefined;
      if (firstField) document.getElementById(generateInputId(firstField))?.focus();
    });
  };

  if (!config) return null;

  const calculatedRows = config.rows.filter((row) => row.calculation);
  const editableRows = config.rows.filter((row) => !row.calculation);

  const getColumnHint = (column?: DynamicTableColumnConfig) =>
    column ? interpolateQuestionText(column.hint, interpolationValues) : undefined;

  const getExcelOptions = (
    title: string,
    columns: DynamicTableColumnConfig[],
  ): DynamicTableExcelOptions => ({
    title,
    rowHeader: config.rowHeader ?? 'Question',
    rows: config.rows,
    columns,
    values: normaliseDynamicTableSubmissionValues(baseConfig!, values, question.rules?.required),
    interpolationValues,
    continuous: config.continuous,
    rowHeaderInput: config.rowHeaderInput,
    showRowHeader,
  });

  const getExcelTables = (): DynamicTableExcelOptions[] => {
    if (config.layout === 'split' && config.groups?.length) {
      return config.groups.map((group) => ({
        ...getExcelOptions(
          group.label,
          group.columnKeys
            .map((key) => config.columns.find((column) => column.key === key))
            .filter((column): column is DynamicTableColumnConfig => Boolean(column)),
        ),
      }));
    }
    return [getExcelOptions(question.title, config.columns)];
  };

  const importExcelTables = (importedTables: Record<string, string>[]) => {
    const importedValues = Object.assign({}, ...importedTables);
    const updatedValues = applyDynamicTableConditions(config, {
      ...values,
      ...importedValues,
    });
    setValues(updatedValues);
    const importedValue = triggerChange(updatedValues);
    void onImport?.(importedValue);
  };

  const getColumnHintId = (column: DynamicTableColumnConfig) => `${anchorId}-${column.key}-hint`;

  const getGroupStartClass = (column: DynamicTableColumnConfig, showGroupSeparators: boolean) => {
    if (!showGroupSeparators) return '';
    const groupIndex =
      config.groups?.findIndex(
        (group) => group.columnKeys[0] === column.key && group.columnKeys.includes(column.key),
      ) ?? -1;
    return groupIndex > 0 ? ' dynamic-table__group-start' : '';
  };

  const renderRows = (
    rows: DynamicTableRowConfig[],
    columns: DynamicTableColumnConfig[],
    showGroupSeparators: boolean,
  ) =>
    rows.map((row) => {
      const rowHint = interpolateQuestionText(row.hint, interpolationValues);
      const rowIsActive = config.highlightActive && activeCell?.rowKey === row.key;
      const rowHeaderField = getDynamicTableRowHeaderFieldName(row.key);
      const hasEditableRowHeader =
        showRowHeader && config.continuous && Boolean(config.rowHeaderInput) && isEditableRow(row);
      const rowIsPlaceholder =
        config.continuous &&
        isEditableRow(row) &&
        editableRows[editableRows.length - 1]?.key === row.key &&
        !isContinuousRowPopulated(config, row.key, values);
      const rowLabel = row.label || 'New row';

      if (rowIsPlaceholder) {
        return (
          <tr key={row.key} className="govuk-table__row dynamic-table__continuous-placeholder">
            <td
              className="govuk-table__cell dynamic-table__add-row-cell"
              colSpan={columns.length + (showRowHeader ? 1 : 0)}
            >
              <button
                type="button"
                className="dynamic-table__add-row-button"
                onClick={() => activateContinuousRow(row)}
                disabled={disabled}
              >
                Click here to add a new line
              </button>
            </td>
          </tr>
        );
      }

      return (
        <tr key={row.key} className="govuk-table__row">
          {showRowHeader && (
            <th
              scope="row"
              className={`govuk-table__header dynamic-table__row-header${
                row.calculation ? ' dynamic-table__total-label' : ''
              }${hasEditableRowHeader ? ' dynamic-table__row-header--input' : ''}${
                rowIsActive ? ' dynamic-table__active-row' : ''
              }`}
            >
              {hasEditableRowHeader && config.rowHeaderInput ? (
                <div className="govuk-form-group">
                  <label
                    className="govuk-label govuk-visually-hidden"
                    htmlFor={generateInputId(rowHeaderField)}
                  >
                    {`${config.rowHeader ?? 'Row name'}, ${rowLabel}`}
                  </label>
                  {config.rowHeaderInput.inputType === 'select' ? (
                    <select
                      className="govuk-select dynamic-table__row-header-input"
                      id={generateInputId(rowHeaderField)}
                      value={values[rowHeaderField] ?? ''}
                      disabled={disabled}
                      onChange={handleRowHeaderChange(row)}
                      onFocus={() =>
                        setActiveCell({
                          rowKey: row.key,
                          columnKey: CONTINUOUS_ROW_HEADER_SUFFIX,
                        })
                      }
                      aria-label={`${config.rowHeader ?? 'Row name'}, ${rowLabel}`}
                    >
                      <option value="">Please select</option>
                      {(config.rowHeaderInput.options ?? []).map((option) => (
                        <option key={option} value={option}>
                          {option}
                        </option>
                      ))}
                    </select>
                  ) : (
                    <textarea
                      className="govuk-input dynamic-table__row-header-input"
                      id={generateInputId(rowHeaderField)}
                      rows={2}
                      value={values[rowHeaderField] ?? ''}
                      disabled={disabled}
                      onChange={handleRowHeaderChange(row)}
                      onFocus={() =>
                        setActiveCell({
                          rowKey: row.key,
                          columnKey: CONTINUOUS_ROW_HEADER_SUFFIX,
                        })
                      }
                      aria-label={`${config.rowHeader ?? 'Row name'}, ${rowLabel}`}
                    />
                  )}
                </div>
              ) : (
                row.label
              )}
              {rowHint && (
                <div className="govuk-hint govuk-!-margin-top-1 govuk-!-margin-bottom-0">
                  {rowHint}
                </div>
              )}
            </th>
          )}
          {columns.map((column) => {
            const field = getDynamicTableFieldName(row.key, column.key);
            const inputDisabled = disabled || !isEditableColumn(column) || !isEditableRow(row);
            const allowDecimal = row.allowDecimal === true || column.allowDecimal === true;
            const allowNegativeNumbers =
              row.allowNegativeNumbers === true || column.allowNegativeNumbers === true;
            const isTextInput = column.inputType === 'text';
            const isSelectInput = column.inputType === 'select';
            const isDurationInput = column.inputType === 'duration' && Boolean(column.duration);
            const inputLabel = `${rowLabel} ${column.label}`;
            const durationUnitField = column.duration
              ? getDynamicTableFieldName(row.key, column.duration.unitKey)
              : '';
            return (
              <td
                key={field}
                className={`govuk-table__cell${getGroupStartClass(
                  column,
                  showGroupSeparators,
                )}${rowIsActive ? ' dynamic-table__active-row' : ''}${
                  column.calculation ? ' dynamic-table__calculated-cell' : ''
                }`}
                onPointerUp={(event) => {
                  if (inputDisabled) return;
                  if (
                    event.target instanceof Element &&
                    event.target.closest('input, select, textarea, button')
                  ) {
                    return;
                  }
                  document.getElementById(generateInputId(field))?.focus();
                }}
              >
                <div className="govuk-form-group">
                  {isDurationInput && column.duration ? (
                    <DynamicTableDurationInput
                      id={generateInputId(field)}
                      label={inputLabel}
                      value={values[field] ?? ''}
                      unitValue={values[durationUnitField] ?? ''}
                      defaultUnit={column.duration.defaultUnit}
                      units={column.duration.units}
                      disabled={inputDisabled}
                      onValueChange={(value) => updateCellValue(row, column, field, value)}
                      onUnitChange={(value) => updateRawCellValue(durationUnitField, value)}
                      onFocus={() => setActiveCell({ rowKey: row.key, columnKey: column.key })}
                    />
                  ) : (
                    <>
                      <label
                        className="govuk-label govuk-visually-hidden"
                        htmlFor={generateInputId(field)}
                      >
                        {inputLabel}
                      </label>
                      {isSelectInput ? (
                        <select
                          className="govuk-select dynamic-table__input--select"
                          id={generateInputId(field)}
                          value={values[field] ?? ''}
                          disabled={inputDisabled}
                          onChange={(event) => updateRawCellValue(field, event.target.value)}
                          onFocus={() => setActiveCell({ rowKey: row.key, columnKey: column.key })}
                          aria-label={inputLabel}
                          aria-describedby={column.hint ? getColumnHintId(column) : undefined}
                        >
                          <option value="">{column.emptyOptionLabel ?? 'Please select'}</option>
                          {(column.options ?? []).map((option) => (
                            <option key={option} value={option}>
                              {option}
                            </option>
                          ))}
                        </select>
                      ) : (
                        <input
                          className={`govuk-input govuk-input--width-5${
                            config.compact ? ' dynamic-table__input' : ''
                          }${isTextInput ? ' dynamic-table__input--text' : ''}`}
                          id={generateInputId(field)}
                          type="text"
                          inputMode={isTextInput ? undefined : allowDecimal ? 'decimal' : 'numeric'}
                          value={values[field] ?? ''}
                          disabled={inputDisabled}
                          onChange={handleValueChange(row, column, field)}
                          onFocus={() => setActiveCell({ rowKey: row.key, columnKey: column.key })}
                          aria-label={inputLabel}
                          aria-describedby={column.hint ? getColumnHintId(column) : undefined}
                          style={{
                            backgroundColor:
                              inputDisabled && !config.compact ? '#b1b4b6' : undefined,
                          }}
                        />
                      )}
                    </>
                  )}
                </div>
              </td>
            );
          })}
        </tr>
      );
    });

  const renderTable = (
    columns: DynamicTableColumnConfig[],
    options: { caption?: string; showGroupHeaders?: boolean } = {},
  ) => {
    const sizingKey = getSizingKey(columns);
    const sizing = tableSizings[sizingKey];
    const hasHorizontalScroll = tableOverflowByKey[sizingKey] === true;
    const columnsWithHints = columns.filter((column) => getColumnHint(column));
    const activeColumn = columns.find((column) => column.key === activeCell?.columnKey);
    const displayedHint = activeColumn
      ? getColumnHint(activeColumn)
      : activeCell
        ? ''
        : getColumnHint(columnsWithHints[0]);
    const visibleGroups = (config.groups ?? [])
      .map((group) => ({
        ...group,
        columns: group.columnKeys
          .map((key) => columns.find((column) => column.key === key))
          .filter((column): column is DynamicTableColumnConfig => Boolean(column)),
      }))
      .filter((group) => group.columns.length > 0);
    const tableClasses = [
      'govuk-table',
      config.compact ? 'dynamic-table--compact' : '',
      config.stickyHeaders ? 'dynamic-table--sticky-headers' : '',
      config.stickyRowHeader && showRowHeader ? 'dynamic-table--sticky-row-header' : '',
      config.addRowBackground === 'lightBlue' ? 'dynamic-table--add-row-light-blue' : '',
    ]
      .filter(Boolean)
      .join(' ');

    return (
      <TableRegion
        className="govuk-!-margin-bottom-6"
        style={
          sizing
            ? ({
                '--dynamic-table-width': `${sizing.tableWidth + tableScrollbarWidth}px`,
              } as CSSProperties)
            : undefined
        }
      >
        {options.caption && (
          <TableCaption>
            <h2 className="govuk-heading-m dynamic-table__caption">{options.caption}</h2>
          </TableCaption>
        )}
        <TableViewport
          data-testid="dynamic-table-viewport"
          data-table-key={sizingKey}
          style={{ maxHeight: config.height }}
          onScroll={(event) => positionBoundedGroupLabels(event.currentTarget)}
        >
          <Table className={tableClasses} style={{ width: sizing?.tableWidth }}>
            <caption className="govuk-visually-hidden">{options.caption ?? question.title}</caption>
            {sizing && (
              <colgroup>
                {showRowHeader && <col style={{ width: sizing.rowHeaderWidth }} />}
                {columns.map((column) => (
                  <col key={column.key} style={{ width: sizing.columnWidths[column.key] }} />
                ))}
              </colgroup>
            )}
            <thead className="govuk-table__head">
              {options.showGroupHeaders && visibleGroups.length > 0 && (
                <tr className="govuk-table__row">
                  {showRowHeader && (
                    <th className="govuk-table__header dynamic-table__row-header" />
                  )}
                  {visibleGroups.map((group, groupIndex) => (
                    <th
                      key={group.key}
                      scope="colgroup"
                      colSpan={group.columns.length}
                      className={`govuk-table__header dynamic-table__group-header${
                        groupIndex > 0 ? ' dynamic-table__group-start' : ''
                      }`}
                    >
                      <span className="dynamic-table__group-label">
                        <span>{group.label}</span>
                      </span>
                    </th>
                  ))}
                </tr>
              )}
              <tr className="govuk-table__row">
                {showRowHeader && (
                  <th scope="col" className="govuk-table__header dynamic-table__row-header">
                    {config.rowHeader ?? 'Question'}
                  </th>
                )}
                {columns.map((column) => {
                  const columnHint = getColumnHint(column);
                  return (
                    <th
                      key={column.key}
                      scope="col"
                      className={`govuk-table__header dynamic-table__column-header${getGroupStartClass(
                        column,
                        options.showGroupHeaders === true,
                      )}${
                        column.headerLineLimit === 1 ? ' dynamic-table__column-header--nowrap' : ''
                      }`}
                    >
                      {column.label}
                      {columnHint && (
                        <span id={getColumnHintId(column)} className="govuk-visually-hidden">
                          {columnHint}
                        </span>
                      )}
                    </th>
                  );
                })}
              </tr>
              {displayedHint && (
                <tr className="govuk-table__row">
                  {showRowHeader && (
                    <th scope="row" className="govuk-table__header dynamic-table__hint-label">
                      Guidance
                    </th>
                  )}
                  <td
                    colSpan={columns.length}
                    className="govuk-table__cell dynamic-table__hint-cell"
                    aria-live="polite"
                  >
                    {displayedHint}
                  </td>
                </tr>
              )}
            </thead>
            <tbody className="govuk-table__body">
              {renderRows(editableRows, columns, options.showGroupHeaders === true)}
            </tbody>
            {calculatedRows.length > 0 && (
              <tfoot>
                {renderRows(calculatedRows, columns, options.showGroupHeaders === true)}
              </tfoot>
            )}
          </Table>
        </TableViewport>
        {hasHorizontalScroll && (
          <p className="govuk-hint govuk-!-margin-top-2 govuk-!-margin-bottom-0">
            Use the bar to scroll left and right to see every column.
          </p>
        )}
      </TableRegion>
    );
  };

  return (
    <div ref={tableRootRef}>
      {error && <ErrorMessage error={error} />}
      {detailsHeading && <Details heading={detailsHeading} content={detailsContent} />}
      {showExcelToolbar && (
        <DynamicTablesExcelToolbar
          title={question.title}
          tables={getExcelTables()}
          disabled={disabled}
          confirmImport={config.confirmExcelImport !== false}
          onImport={importExcelTables}
        />
      )}
      {config.layout === 'split' && config.groups?.length
        ? config.groups.map((group) => {
            const groupColumns = group.columnKeys
              .map((key) => config.columns.find((column) => column.key === key))
              .filter((column): column is DynamicTableColumnConfig => Boolean(column));
            return <div key={group.key}>{renderTable(groupColumns, { caption: group.label })}</div>;
          })
        : renderTable(config.columns, { showGroupHeaders: Boolean(config.groups?.length) })}
    </div>
  );
}

export default DynamicTable;
